import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { LogOut } from 'lucide-react'
import AuthShell from '../../components/AuthShell'

export default function AccountPickerPage() {
  const navigate = useNavigate()
  const [contexts, setContexts] = useState([])
  const [loading, setLoading]   = useState(true)
  const [user, setUser]         = useState(null)

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { navigate('/login'); return }
      setUser(user)

      const found = []

      // Check own wedding(s)
      const { data: weddings } = await supabase
        .from('weddings')
        .select('id, slug, partner1_name, partner2_name, wedding_date, partner1_id')
        .or(`partner1_id.eq.${user.id},partner2_id.eq.${user.id}`)
      for (const w of weddings || []) {
        found.push({
          type: 'wedding',
          id: w.id,
          slug: w.slug,
          title: `${w.partner1_name} & ${w.partner2_name}`,
          sub: w.wedding_date ? new Date(w.wedding_date).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}) : 'Date not set',
          role: w.partner1_id === user.id ? 'Partner 1' : 'Partner 2',
          emoji: '💍',
          action: () => { window.location.href = '/dashboard' }
        })
      }

      // Check ambassador roles — two separate queries to avoid FK join issues
      const { data: ambassadorRoles } = await supabase
        .from('asoebi_ambassadors')
        .select('id, wedding_id')
        .eq('email', user.email)
      for (const a of ambassadorRoles || []) {
        const { data: w } = await supabase.rpc('get_public_wedding', { p_id: a.wedding_id })
        if (!w) continue
        found.push({
          type: 'ambassador',
          id: a.id,
          slug: w.slug,
          title: `${w.partner1_name} & ${w.partner2_name}`,
          sub: 'Aso Ebi Ambassador',
          role: 'Ambassador',
          emoji: '🧵',
          action: () => {
            // Store ambassador context — dashboard reads this to show limited view
            sessionStorage.setItem('ambassador_wedding_slug', w.slug)
            sessionStorage.setItem('ambassador_mode', '1')
            window.location.href = `/dashboard/asoebi`
          }
        })
      }

      // If only one context, go directly
      if (found.length === 1) { found[0].action(); return }
      // If no context, go to onboarding
      if (found.length === 0) { navigate('/onboarding'); return }

      setContexts(found)
      setLoading(false)
    })()
  }, [navigate])

  async function signOut() {
    await supabase.auth.signOut()
    navigate('/login')
  }

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>Loading your accounts…</p>
    </div>
  )

  return (
    <AuthShell image="silhouette-heart" tagline="Open your celebration." sub="Your weddings and roles, all in one place.">

      {/* Logo */}
      <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:36, marginBottom:'2rem' }}/>

      {/* Heading */}
      <div style={{ textAlign:'center', marginBottom:'2rem' }}>
        <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.75rem', color:'#1A1A1A', margin:'0 0 0.4rem', letterSpacing:'-0.03em' }}>Welcome back</h1>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#9B9B9B', margin:0 }}>
          {user?.email} · Which account would you like to open?
        </p>
      </div>

      {/* Context cards */}
      <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem', width:'100%', maxWidth:400 }}>
        {contexts.map((ctx, i) => (
          <button key={i} onClick={ctx.action}
            style={{ background:'white', border:'1.5px solid #E5E0D8', borderRadius:'1.25rem', padding:'1.25rem 1.5rem', cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:'1rem', transition:'all 0.2s', boxShadow:'0 2px 12px rgba(0,0,0,0.04)' }}
            onMouseEnter={e=>{ e.currentTarget.style.borderColor='rgba(196,130,106,0.5)'; e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.08)' }}
            onMouseLeave={e=>{ e.currentTarget.style.borderColor='#E5E0D8'; e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.04)' }}>

            {/* Avatar */}
            <div style={{ width:52, height:52, borderRadius:'50%', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.5rem',
              background: ctx.type==='wedding' ? 'linear-gradient(135deg,rgba(196,130,106,0.15),rgba(155,175,155,0.15))' : 'linear-gradient(135deg,rgba(196,130,106,0.12),rgba(244,179,0,0.12))',
              border: ctx.type==='wedding' ? '1.5px solid rgba(196,130,106,0.2)' : '1.5px solid rgba(196,130,106,0.25)' }}>
              {ctx.emoji}
            </div>

            {/* Info */}
            <div style={{ flex:1, minWidth:0 }}>
              <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', color:'#1A1A1A', margin:'0 0 0.15rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ctx.title}</p>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:'0 0 0.25rem' }}>{ctx.sub}</p>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', fontWeight:700,
                color: ctx.type==='wedding' ? '#C4826A' : '#C4826A',
                background: ctx.type==='wedding' ? 'rgba(196,130,106,0.1)' : 'rgba(196,130,106,0.1)',
                borderRadius:100, padding:'2px 8px' }}>
                {ctx.role}
              </span>
            </div>

            {/* Arrow */}
            <span style={{ color:'#BCBCBC', fontSize:'1.1rem', flexShrink:0 }}>→</span>
          </button>
        ))}
      </div>

      {/* Sign out */}
      <button onClick={signOut} style={{ marginTop:'2rem', display:'flex', alignItems:'center', gap:'0.4rem', background:'none', border:'none', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#BCBCBC' }}>
        <LogOut size={13}/> Sign out
      </button>
    </AuthShell>
  )
}
